import type { ComponentType } from "react";

export type MarketingRouteKey =
  | "home"
  | "products"
  | "satelliteProducts"
  | "about"
  | "markets"
  | "marketDetail"
  | "trVoice"
  | "trVision"
  | "trIot"
  | "trFlow";

export const marketingRouteLoaders = {
  home: () => import("../home/HomePage"),
  products: () => import("../products/ProductsPage"),
  satelliteProducts: () => import("../satellite-products/SatelliteProductsPage"),
  about: () => import("../about/AboutPage"),
  markets: () => import("../markets/MarketsPage"),
  marketDetail: () => import("../markets/MarketDetailPage"),
  trVoice: () => import("../tr-voice/TrVoicePage"),
  trVision: () => import("../tr-vision/TrVisionPage"),
  trIot: () => import("../tr-iot/TrIotPage"),
  trFlow: () => import("../tr-flow/TrFlowPage"),
} satisfies Record<MarketingRouteKey, () => Promise<{ default: ComponentType }>>;

const MARKET_IDS = new Set([
  "cities",
  "buildings",
  "transport",
  "retail",
  "infrastructure",
  "government",
]);

const EXACT_ROUTE_KEYS: Record<string, MarketingRouteKey> = {
  "/": "home",
  "/products": "products",
  "/satellite-products": "satelliteProducts",
  "/about": "about",
  "/markets": "markets",
  "/product/trvoice": "trVoice",
  "/product/trvision": "trVision",
  "/product/iot": "trIot",
  "/product/trflow": "trFlow",
};

const PRODUCT_HEROES: Record<string, string> = {
  "/product/trvoice": "/Images/products/tr-voice-visual.webp",
  "/product/trvision": "/Images/products/tr-vision-visual.webp",
  "/product/iot": "/Images/products/tr-iot-visual.webp",
  "/product/trflow": "/Images/products/tr-flow-visual.webp",
};

const normalizePathname = (pathname: string): string => {
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "");
};

export const getMarketingRouteKey = (pathname: string): MarketingRouteKey | undefined => {
  const normalizedPathname = normalizePathname(pathname);
  const exactRoute = EXACT_ROUTE_KEYS[normalizedPathname];
  if (exactRoute) return exactRoute;

  const marketMatch = normalizedPathname.match(/^\/markets\/([^/]+)$/);
  return marketMatch && MARKET_IDS.has(marketMatch[1]) ? "marketDetail" : undefined;
};

const getViewportAsset = (desktop: string, mobile: string, breakpoint: number): string =>
  window.matchMedia(`(max-width: ${breakpoint}px)`).matches ? mobile : desktop;

export const getMarketingRouteHero = (pathname: string): string | undefined => {
  const normalizedPathname = normalizePathname(pathname);

  if (normalizedPathname === "/products") {
    return getViewportAsset(
      "/Images/products/products_hero.webp",
      "/Images/products/products_hero_mobile.webp",
      768
    );
  }
  if (normalizedPathname === "/about") {
    return getViewportAsset(
      "/Images/about/about_hero.webp",
      "/Images/about/about_hero_mobile.webp",
      860
    );
  }
  if (normalizedPathname === "/markets") {
    return getViewportAsset(
      "/Images/markets/hero.jpg",
      "/Images/markets/hero-mobile.webp",
      860
    );
  }
  if (normalizedPathname === "/satellite-products") {
    return getViewportAsset(
      "/Images/satellite-products-hero.webp",
      "/Images/satellite-products-hero-mobile.webp",
      620
    );
  }

  const marketMatch = normalizedPathname.match(/^\/markets\/([^/]+)$/);
  if (marketMatch && MARKET_IDS.has(marketMatch[1])) {
    const marketId = marketMatch[1];
    return getViewportAsset(
      `/Images/markets/${marketId}.jpg`,
      `/Images/markets/${marketId}-mobile.webp`,
      620
    );
  }

  return PRODUCT_HEROES[normalizedPathname];
};
